import { useState, useCallback } from 'react';
import { useGetUnreconciledInvoicesQuery, useReconcileInvoicesMutation } from '../Service/reportsApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import type { UnreconciledInvoiceRowDto, GetUnreconciledInvoicesParams } from '../Types/reports.types';

export const useReservationPaymentReconciliationPage = () => {
  const [filters, setFilters] = useState<GetUnreconciledInvoicesParams>({ pageNumber: 1, pageSize: 20 });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [previewRows, setPreviewRows] = useState<UnreconciledInvoiceRowDto[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [operationId, setOperationId] = useState('');
  const [reconcileError, setReconcileError] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError, error } = useGetUnreconciledInvoicesQuery(filters);
  const [reconcileInvoices, { isLoading: isReconciling }] = useReconcileInvoicesMutation();

  const queryErrorMessage = isError ? extractApiErrorMessage(error, 'Nie udało się pobrać raportu.') : null;

  const openSingleDialog = useCallback((row: UnreconciledInvoiceRowDto) => {
    setPreviewRows([row]);
    setOperationId(globalThis.crypto.randomUUID());
    setReconcileError(null);
    setDialogOpen(true);
  }, []);

  const openBatchDialog = useCallback(
    (rows: UnreconciledInvoiceRowDto[]) => {
      setPreviewRows(rows);
      setOperationId(globalThis.crypto.randomUUID());
      setReconcileError(null);
      setDialogOpen(true);
    },
    []
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const confirmReconcile = useCallback(async () => {
    const invoiceIds = previewRows.map((r) => r.invoiceId);
    try {
      await reconcileInvoices({ operationId, invoiceIds }).unwrap();
      setDialogOpen(false);
      setSelectedIds([]);
    } catch (e) {
      setReconcileError(extractApiErrorMessage(e, 'Rozliczenie nie powiodło się.'));
    }
  }, [previewRows, operationId, reconcileInvoices]);

  const toggleSelection = useCallback((id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleFilterChange = useCallback((next: Partial<GetUnreconciledInvoicesParams>) => {
    setFilters((prev) => ({ ...prev, ...next, pageNumber: 1 }));
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setFilters((prev) => ({ ...prev, pageNumber }));
  }, []);

  const selectedRows = data?.items.filter((r) => selectedIds.includes(r.invoiceId)) ?? [];

  return {
    data,
    isLoading,
    isFetching,
    queryErrorMessage,
    filters,
    selectedIds,
    selectedRows,
    previewRows,
    dialogOpen,
    isReconciling,
    reconcileError,
    openSingleDialog,
    openBatchDialog,
    closeDialog,
    confirmReconcile,
    toggleSelection,
    handleFilterChange,
    handlePageChange,
  };
};
